import { format } from "date-fns";
import Highcharts, { GradientColorObject, Options } from "highcharts";
import { DeviceTypesEnum } from "../../common/enums/DeviceTypesEnum";
import { LiquidProductionTATEnum } from "../../common/enums/LiquidProductionTATEnum";
import { StandaloneTankTATEnum } from "../../common/enums/StandaloneTankTATEnum";
import { IEntityValueDeviceModel, IEntityValueTankModel } from "../../common/models/IEntityValueModels";
import { DATE_FORMAT_STRING, TIME_FORMAT_STRING } from "../../common/utilities/TimeframeUtilities";
import i18n from "../../services/localization-service/i18n";
import { Theme } from "@mui/material";
import { themifyHighchartsOptions } from "../../common/utilities/ThemifyHighchartsOptions";


export interface ITankTagDisplayValue {
    name: string,
    value: string,
    date: string;
    time?: string,
    tatID?: number
}



const perShapeGradient = {
    x1: 0,
    y1: 0,
    x2: 1,
    y2: 0
};


export const chartColors: GradientColorObject[] = [{
    linearGradient: perShapeGradient,
    stops: [
        [0, 'rgb(128,0,0)'],
        [0.1, 'rgb(204,0,0)'],
        [0.25, 'rgb(245,128,128)'],
        [0.5, 'rgb(230,75,75)'],
        [0.75, 'rgb(220,50,50)'],
        [0.9, 'rgb(230,75,75)'],
        [1, 'rgb(128,0,0)']
    ]
}, {
    linearGradient: Highcharts.merge(perShapeGradient),
    stops: [
        [0, 'rgb(46, 150, 208)'],
        [0.5, 'rgb(120, 202, 248)'],
        [1, 'rgb(46, 150, 208)']
    ]
}, {
    linearGradient: Highcharts.merge(perShapeGradient),
    stops: [
        [0, 'rgb(112, 180, 5)'],
        [0.5, 'rgb(136, 219, 5)'],
        [1, 'rgb(112, 180, 5)'],
    ]
}, {
    linearGradient: Highcharts.merge(perShapeGradient),
    stops: [
        [0, 'rgba(192, 192, 192, .5)'],
        [0.5, 'rgba(245, 245, 245, .5)'],
        [1, 'rgba(192, 192, 192, .5)'],
    ]
}];


function getLiquidProductionChartOptions(
    tank: IEntityValueTankModel,
    legendVertical: boolean,
    theme: Theme,useTankRenderingV2: boolean
): Options {

    let oilStockBarrels = tank.tags.find(o => o.tatID === LiquidProductionTATEnum.OilStock)?.tagValue?.value ?? 0;
    let oilHeightInches = tank.tags.find(o => o.tatID === LiquidProductionTATEnum.OilHeight)?.tagValue?.value ?? 0;
    let oilHeightFeet = oilHeightInches / 12;

    let tankCapacityBarrels = tank.tankStyle.totalBarrels;
    let tankHeightFeet = tank.tankStyle.heightInInches / 12;

    let waterStockBarrels = tank.tags.find(o => o.tatID === LiquidProductionTATEnum.WaterStock)?.tagValue?.value ?? 0;
    let waterHeightInches = tank.tags.find(o => o.tatID === LiquidProductionTATEnum.WaterHeight)?.tagValue?.value ?? 0;
    let waterHeightFeet = waterHeightInches / 12;


    let perShapeGradient = {
        x1: 0,
        y1: 0,
        x2: 1,
        y2: 0
    };

    //???let colors: any = Highcharts.getOptions().colors;

    let colors: GradientColorObject[] = [{
        linearGradient: perShapeGradient,
        stops: [
            [0, 'rgb(128,0,0)'],
            [0.1, 'rgb(204,0,0)'],
            [0.25, 'rgb(245,128,128)'],
            [0.5, 'rgb(230,75,75)'],
            [0.75, 'rgb(220,50,50)'],
            [0.9, 'rgb(230,75,75)'],
            [1, 'rgb(128,0,0)']
        ]
    }, {
        //linearGradient: (<any>Highcharts).merge(perShapeGradient),
        linearGradient: Highcharts.merge(perShapeGradient),
        stops: [
            [0, 'rgb(46, 150, 208)'],
            [0.5, 'rgb(120, 202, 248)'],
            [1, 'rgb(46, 150, 208)']
        ]
    }, {
        //linearGradient: (<any>Highcharts).merge(perShapeGradient),
        linearGradient: Highcharts.merge(perShapeGradient),
        stops: [
            [0, 'rgb(112, 180, 5)'],
            [0.5, 'rgb(136, 219, 5)'],
            [1, 'rgb(112, 180, 5)'],
        ]
    }, {
        //linearGradient: (<any>Highcharts).merge(perShapeGradient),
        linearGradient: Highcharts.merge(perShapeGradient),
        stops: [
            [0, 'rgba(192, 192, 192, .5)'],
            [0.5, 'rgba(245, 245, 245, .5)'],
            [1, 'rgba(192, 192, 192, .5)'],
        ]
    }];

    const leftAxisUnits: string = "bbls";
    const rightAxisUnits: string = "Feet";

    //const series: Highcharts.SeriesOptions[] = [];
    const series: Highcharts.SeriesCylinderOptions[] = [];

    const origOilBarrels = oilStockBarrels;
    const origWaterBarrels = waterStockBarrels;

    const remainingCapacity: number = Math.abs(tankCapacityBarrels - oilStockBarrels - waterStockBarrels);
    const overflow: boolean = (oilStockBarrels + waterStockBarrels) > tankCapacityBarrels;

    if (!overflow) {
        series.push({
            color: colors[3],
            type: "cylinder",
            data: [remainingCapacity],
            name: i18n.t('liquidProd.tankRemainingCapacity') as string,
            yAxis: 0,
            showInLegend: !useTankRenderingV2,
        });
    } else {

        if (oilStockBarrels) {
            oilStockBarrels -= remainingCapacity;
        } else {
            waterStockBarrels -= remainingCapacity;
        }

        series.push({
            color: colors[0],
            type: "cylinder",
            data: [remainingCapacity],
            name: i18n.t("liquidProd.tankOverflow") as string,
            yAxis: 0,
            showInLegend: !useTankRenderingV2,
        });
    }

    if (oilStockBarrels) {
        series.push({
            color: colors[2],
            type: "cylinder",
            data: [oilStockBarrels],
            name: i18n.t("liquidProd.oil") as string,
            yAxis: 0,
            showInLegend: !useTankRenderingV2,
        });
    }

    if (waterStockBarrels) {
        series.push({
                color: colors[1],
                type: "cylinder",
                data: [waterStockBarrels],
                name: i18n.t("liquidProd.water") as string,
                yAxis: 0,
                showInLegend: !useTankRenderingV2,
        });
    }

    const bblsMin: number = Math.min(0, oilStockBarrels, waterStockBarrels);
    const bblsMax: number = Math.max((origOilBarrels + origWaterBarrels), tankCapacityBarrels);

    const feetMin: number = Math.min(0, oilHeightFeet, waterHeightFeet);
    const feetMax: number = Math.max((oilHeightFeet + waterHeightFeet), tankHeightFeet);

    let options: Options = {
        chart: {
            ignoreHiddenSeries: false,
            //marginTop: !useTankRenderingV2 || isPhone ? 50 : undefined,
            marginTop: 50,
            type: "cylinder",
            options3d: {
                enabled: true,
                alpha: 15,
                beta: 0, //15,
                depth: 50,
                viewDistance: 25
            },
            //margin: useTankRenderingV2 ? [0, 0, 0, 0] : undefined
        },
        credits: {
            enabled: false
        },
        exporting: {
            enabled: false
        },
         title: {
             text: undefined
        },
        plotOptions: {
            cylinder: {
                pointWidth: 300,
                stacking: 'normal',
                depth: 300,
            }
        },
        legend: {
            layout: !useTankRenderingV2 ? legendVertical ? 'vertical' : 'horizontal' : undefined,
            enabled: !useTankRenderingV2,
            //layout: 'vertical'

        },
        series: series,
        // tooltip: {
        //     headerFormat: null
        // },
        xAxis: [{
            visible: false
        }],
        yAxis: [{
            //alignTicks: true,
            visible: !useTankRenderingV2,
            endOnTick: false,
            labels: {
                format: "{value} " + leftAxisUnits,
            },
            min: bblsMin,
            max: bblsMax,
            plotLines: [{
                color: "#D3D3D3",
                dashStyle: "Solid",
                value: tankCapacityBarrels,
                width: 1,
                zIndex: 999999999
            }],
            title: {
                text: undefined
            }
        },{
            visible: !useTankRenderingV2,
            //alignTicks: true,
            endOnTick: false,
            gridLineWidth: 0,
            labels: {
                format: "{value} " + rightAxisUnits,
            },
            min: feetMin,
            max: feetMax,
            opposite: true,
            showEmpty: true,
            title: {
                text: null
            }
        }]
    };

    return themifyHighchartsOptions(options, theme)
}

function getStandAloneTankChartOptions(
    tank: IEntityValueDeviceModel,
    theme: Theme,
    useTankRenderingV2: boolean
): Options {

    let stockTag = tank.tags.find(o => o.tatID ===  StandaloneTankTATEnum.LiquidStock);
    let stock = stockTag?.tagValue?.value ?? 0;
    let heightInches = tank.tags.find(o => o.tatID === StandaloneTankTATEnum.LiquidHeight)?.tagValue?.value ?? 0;
    let heightFeet = heightInches / 12;

    let tankCapacity = tank.tankStyle.totalBarrels;
    let tankHeightFeet = tank.tankStyle.heightInInches / 12;

    // let perShapeGradient = {
    //     x1: 0,
    //     y1: 0,
    //     x2: 1,
    //     y2: 0
    // };

    // let colors: GradientColorObject[] = [{
    //     linearGradient: perShapeGradient,
    //     stops: [
    //         [0, 'rgb(128,0,0)'],
    //         [0.1, 'rgb(204,0,0)'],
    //         [0.25, 'rgb(245,128,128)'],
    //         [0.5, 'rgb(230,75,75)'],
    //         [0.75, 'rgb(220,50,50)'],
    //         [0.9, 'rgb(230,75,75)'],
    //         [1, 'rgb(128,0,0)']
    //     ]
    // }, {
    //     linearGradient: Highcharts.merge(perShapeGradient),
    //     stops: [
    //         [0, 'rgb(46, 150, 208)'],
    //         [0.5, 'rgb(120, 202, 248)'],
    //         [1, 'rgb(46, 150, 208)']
    //     ]
    // }, {
    //     linearGradient: Highcharts.merge(perShapeGradient),
    //     stops: [
    //         [0, 'rgb(112, 180, 5)'],
    //         [0.5, 'rgb(136, 219, 5)'],
    //         [1, 'rgb(112, 180, 5)'],
    //     ]
    // }, {
    //     linearGradient: Highcharts.merge(perShapeGradient),
    //     stops: [
    //         [0, 'rgba(192, 192, 192, .5)'],
    //         [0.5, 'rgba(245, 245, 245, .5)'],
    //         [1, 'rgba(192, 192, 192, .5)'],
    //     ]
    // }];

    const leftAxisUnits: string = stockTag?.tagValue?.unitDisplayName ?? "bbls";
    
    const rightAxisUnits: string = "Feet";

    const series: Highcharts.SeriesCylinderOptions[] = [];

    const origStock = stock;
    
    const remainingCapacity: number = Math.abs(tankCapacity - stock);
    const overflow: boolean = stock > tankCapacity;

    if (!overflow) {
        series.push({
            color: chartColors[3],
            type: "cylinder",
            data: [remainingCapacity],
            name: i18n.t('liquidProd.tankRemainingCapacity') as string,
            yAxis: 0,
            showInLegend: !useTankRenderingV2,
        });
    } else {

        stock -= remainingCapacity;

        series.push({
            color: chartColors[0],
            type: "cylinder",
            data: [remainingCapacity],
            name: i18n.t("liquidProd.tankOverflow") as string,
            yAxis: 0,
            showInLegend: !useTankRenderingV2,
        });
    }


    series.push({
        color: tank.subTypeName?.toLowerCase() === 'water'? chartColors[1] : chartColors[2],
        type: "cylinder",
        data: [stock],
        name: tank.subTypeName,
        yAxis: 0,
        showInLegend: !useTankRenderingV2,
    });

    const stockMin: number = Math.min(0, stock);
    const stockMax: number = Math.max(origStock, tankCapacity);

    const feetMin: number = Math.min(0, heightFeet);
    const feetMax: number = Math.max(heightFeet, tankHeightFeet);

    let options: Options = {
        chart: {
            ignoreHiddenSeries: false,
            marginTop: 50,
            type: "cylinder",
            options3d: {
                enabled: true,
                alpha: 15,
                beta: 0, //15,
                depth: 50,
                viewDistance: 25
            },
        },
        credits: {
            enabled: false
        },
        exporting: {
            enabled: false
        },
         title: {
             text: undefined
        },
        plotOptions: {
            cylinder: {
                pointWidth: 300,
                stacking: 'normal',
                depth: 300,
            }
        },
        series: series,
        xAxis: [{
            visible: false
        }],
        yAxis: [{
            visible: !useTankRenderingV2,
            //alignTicks: true,
            endOnTick: false,
            labels: {
                format: "{value} " + leftAxisUnits,
            },
            min: stockMin,
            max: stockMax,
            plotLines: [{
                color: "#D3D3D3",
                dashStyle: "Solid",
                value: tankCapacity,
                width: 1,
                zIndex: 999999999
            }],
            title: {
                text: undefined
            }
        },{
            //alignTicks: true,
            endOnTick: false,
            gridLineWidth: 0,
            labels: {
                format: "{value} " + rightAxisUnits,
            },
            min: feetMin,
            max: feetMax,
            opposite: true,
            showEmpty: true,
            title: {
                text: null
            }
        }]
    };

    return themifyHighchartsOptions(options, theme);
}

function getElevatedTankChartOptions(
    tank: IEntityValueDeviceModel,
    theme: Theme,
    useTankRenderingV2: boolean
): Options {

    const stockTag = tank.tags.find(o => o.tatID ===  StandaloneTankTATEnum.LiquidStock);
    let stock = stockTag?.tagValue?.value ?? 0;

    // const liquidHeightTag = tank.tags.find(o => o.tatID === StandaloneTankTATEnum.LiquidHeight);
    // const liquidHeightFeet = liquidHeightTag?.uomID === 19 // feet
    //     ? liquidHeightTag.tagValue?.value ?? 0
    //     : (liquidHeightTag?.tagValue?.value ?? 0) / 12;

    const tankCapacity = tank.tankStyle.totalBarrels;
    const tankBottomHeightFeet = (tank.tankStyle.elevationInInches ?? 0) / 12;
    const tankTopHeightFeet = tankBottomHeightFeet + (tank.tankStyle.heightInInches / 12);

    const leftAxisUnits: string = stockTag?.tagValue?.unitDisplayName ?? "bbls";
    
    const rightAxisUnits: string = "Feet";

    const series: Highcharts.SeriesCylinderOptions[] = [];

    const origStock = stock;
    
    const remainingCapacity: number = Math.abs(tankCapacity - stock);
    const overflow: boolean = stock > tankCapacity;

    if (!overflow) {
        series.push({
            color: chartColors[3],
            type: "cylinder",
            data: [remainingCapacity],
            name: i18n.t('liquidProd.tankRemainingCapacity') as string,
            yAxis: 0,
            showInLegend: !useTankRenderingV2,
        });
    } else {

        stock -= remainingCapacity;

        series.push({
            color: chartColors[0],
            type: "cylinder",
            data: [remainingCapacity],
            name: i18n.t("liquidProd.tankOverflow") as string,
            yAxis: 0,
            showInLegend: !useTankRenderingV2,
        });
    }

    series.push({
        color: tank.subTypeName?.toLowerCase() === 'water'? chartColors[1] : chartColors[2],
        type: "cylinder",
        data: [stock],
        name: tank.subTypeName,
        yAxis: 0,
        showInLegend: !useTankRenderingV2,
    });

    const stockMin: number = Math.min(0, stock);
    const stockMax: number = Math.max(origStock, tankCapacity);

    const feetMin: number = tankBottomHeightFeet; //Math.min(tankBottomHeightFeet, liquidHeightFeet);
    const feetMax: number = tankTopHeightFeet; //Math.max(liquidHeightFeet, tankTopHeightFeet);

    let options: Options = {
        chart: {
            ignoreHiddenSeries: false,
            marginTop: 50,
            type: "cylinder",
            options3d: {
                enabled: true,
                alpha: 15,
                beta: 0, //15,
                depth: 50,
                viewDistance: 25
            },
        },
        credits: {
            enabled: false
        },
        exporting: {
            enabled: false
        },
         title: {
             text: undefined
        },
        plotOptions: {
            cylinder: {
                pointWidth: 300,
                stacking: 'normal',
                depth: 300,
            }
        },
        series: series,
        xAxis: [{
            visible: false
        }],
        yAxis: [{
            visible: !useTankRenderingV2,
            //alignTicks: true,
            endOnTick: false,
            labels: {
                format: "{value} " + leftAxisUnits,
            },
            min: stockMin,
            max: stockMax,
            plotLines: [{
                color: "#D3D3D3",
                dashStyle: "Solid",
                value: tankCapacity,
                width: 1,
                zIndex: 999999999
            }],
            title: {
                text: undefined
            }
        },{
            //alignTicks: true,
            endOnTick: false,
            gridLineWidth: 0,
            labels: {
                format: "{value} " + rightAxisUnits,
            },
            min: feetMin,
            max: feetMax,
            opposite: true,
            showEmpty: true,
            title: {
                text: null
            }
        }]
    };

    return themifyHighchartsOptions(options, theme);
}

const TankCylinderUtils = {
    getChartOptions: function(
        tank: IEntityValueTankModel | IEntityValueDeviceModel,
        legendVertical: boolean,
        theme: Theme,
        useTankRenderingV2: boolean
    ): Options {
        if (tank.deviceType === DeviceTypesEnum.InternalTank) {
            return getLiquidProductionChartOptions(tank, legendVertical, theme, useTankRenderingV2);

        } else {
            //return getStandAloneTankChartOptions(tank, theme, useTankRenderingV2)
            return getElevatedTankChartOptions(tank, theme, useTankRenderingV2)
        }
    },
    getTagDisplayText: function(tank: IEntityValueTankModel, tat: number) {

        let label: string = '';

        switch(tat) {
            case LiquidProductionTATEnum.OilStock:
                label = i18n.t('liquidProd.oilStock');
                break;

            case LiquidProductionTATEnum.WaterStock:
                label = i18n.t('liquidProd.waterStock');
                break;

            case LiquidProductionTATEnum.TopLevel:
                label = i18n.t('liquidProd.topLevel');
                break;

            case LiquidProductionTATEnum.InterfaceLevel:
                label = i18n.t('liquidProd.interfaceLevel');
                break;

            case StandaloneTankTATEnum.LiquidStock:
                label = i18n.t('objectTagValues.liquidStock');
                break;

            case StandaloneTankTATEnum.LiquidHeight:
                label = i18n.t('objectTagValues.liquidHeight');
                break;

            default:
                break;                
        }

        let tag = tank.tags.find(o => o.tatID === tat);
        let value = tag?.tagValue?.displayText;

        let date = '...';
        let time = '';

        if (value) {
            date = format(new Date(tag?.tagValue?.timestamp as string), DATE_FORMAT_STRING);
            time = format(new Date(tag?.tagValue?.timestamp as string), TIME_FORMAT_STRING);
        } 

        const item: ITankTagDisplayValue = {
            name: label,
            value: value ? `${value} ${tag?.tagValue?.unitDisplayName}` : '...',
            date: date,
            time: time,
            tatID: tat
        };

        return item;
    }

};

export default TankCylinderUtils;
